import React, { useState } from "react";
import {
  ShoppingCart,
  Star,
  Users,
  Shield,
  Zap,
  Heart,
  ChevronRight,
  Check,
  Phone,
  Mail,
  Award,
  Droplet,
  Clock,
  Target,
  CheckCircle,
  AlertCircle,
  Menu,
  X,
  Trash2,
  Plus,
  Minus,
} from "lucide-react";

const IMG = {
  logo: "https://i.imgur.com/OZa3rAL.png",
  product: "https://i.imgur.com/kKFdNgO.jpeg",
  gallery: [
    "https://i.imgur.com/LoUAtDy.jpeg",
    "https://i.imgur.com/9UtDRIO.jpeg",
    "https://i.imgur.com/Zb5ueiz.jpeg",
    "https://i.imgur.com/cv7BGZj.jpeg",
    "https://i.imgur.com/KSL0vuF.jpeg",
    "https://i.imgur.com/kKFdNgO.jpeg",
  ],
};

const STRIPE_CONFIG = {
  publishableKey:
    "pk_live_51SKWcD4Ruyzrq8XLQN2zItxst1EuLMriQIA2p5hYtOs9VHlziGpIFKt0Feh4ebg1XtLHoTeYIftzaftd2qrjRblJ00sKOuKxYv",
  priceIds: {
    single: "price_1SNoQC4Ruyzrq8XLAmVnD1sz",
    starter: "price_1SNoQv4Ruyzrq8XLnbGM6m7D",
    value: "price_1SNoRR4Ruyzrq8XLXfiFhTra",
    family: "price_1SNoRs4Ruyzrq8XLW5HjFLU0",
  },
};

export default function GlucoNowWebsite() {
  const [activePage, setActivePage] = useState("shop");
  const [selectedPack, setSelectedPack] = useState("single");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const packs = {
    single: { qty: 1, price: 20, label: "Single Bottle", save: 0 },
    starter: { qty: 3, price: 54, label: "Starter Pack", save: 10 },
    value: { qty: 6, price: 96, label: "Value Pack", save: 20 },
    family: { qty: 12, price: 168, label: "Family Pack", save: 30 },
  };

  const addToCart = (packKey) => {
    const pack = packs[packKey];
    const existingItem = cart.find((i) => i.id === packKey);
    if (existingItem) {
      setCart(
        cart.map((i) =>
          i.id === packKey ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      setCart([
        ...cart,
        {
          id: packKey,
          name: pack.label,
          price: pack.price,
          bottles: pack.qty,
          quantity: 1,
        },
      ]);
    }
  };

  const removeFromCart = (packKey) =>
    setCart(cart.filter((i) => i.id !== packKey));

  const updateQuantity = (packKey, change) =>
    setCart(
      cart.map((i) =>
        i.id === packKey
          ? { ...i, quantity: Math.max(1, i.quantity + change) }
          : i
      )
    );

  const getCartTotal = () => cart.reduce((t, i) => t + i.price * i.quantity, 0);
  const getCartCount = () => cart.reduce((t, i) => t + i.quantity, 0);

  const features = [
    {
      icon: Droplet,
      title: "Natural Ingredients",
      desc: "Plant-based compounds traditionally known for blood sugar support",
    },
    {
      icon: Heart,
      title: "Tastes Like Wellness",
      desc: "Smooth berry flavour—refreshing with no bitterness",
    },
    {
      icon: Clock,
      title: "Simple & Convenient",
      desc: "Just 50ml after meals. Shake, pour, support your health",
    },
    {
      icon: Target,
      title: "Built for Singapore",
      desc: "Designed with local dietary habits and lifestyle in mind",
    },
  ];

  const reviews = [
    {
      name: "Mr. Tan, 52",
      subtitle: "Type 2 diabetic for 8 years",
      rating: 5,
      text: "After just 3 weeks of taking GlucoNow daily, I noticed a real difference in my sugar readings—especially after meals. It fits so easily into my lifestyle, and I love that it's not a pill.",
    },
  ];
  const CheckoutModal = () => {
    const [formData, setFormData] = useState({
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      postal: "",
    });

    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsProcessing(true);

      try {
        const stripe = window.Stripe(STRIPE_CONFIG.publishableKey);
        const lineItems = cart.map((item) => ({
          price: STRIPE_CONFIG.priceIds[item.id],
          quantity: item.quantity,
        }));

        const response = await fetch(
          "https://gluconow.dragomainacc.workers.dev/create-checkout",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              lineItems,
              customerInfo: {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                address: `${formData.address}, ${formData.city}, ${formData.postal}`,
              },
              successUrl: window.location.origin + "/?success=true",
              cancelUrl: window.location.origin + "/?canceled=true",
            }),
          }
        );

        const session = await response.json();

        if (session.error) {
          alert("Error: " + session.error);
          setIsProcessing(false);
          return;
        }

        const result = await stripe.redirectToCheckout({
          sessionId: session.id,
        });

        if (result.error) {
          alert(result.error.message);
          setIsProcessing(false);
        }
      } catch (error) {
        console.error("Checkout error:", error);
        alert("Payment failed. Please try again.");
        setIsProcessing(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 flex justify-between items-center rounded-t-3xl">
            <h2 className="text-2xl font-bold">Secure Checkout</h2>
            <button
              onClick={() => setShowCheckout(false)}
              className="p-2 hover:bg-white/20 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Order Summary</h3>
              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                  >
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        Qty: {item.quantity} × SGD ${item.price}
                      </p>
                    </div>
                    <p className="font-bold text-emerald-600">
                      SGD ${item.price * item.quantity}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>SGD ${getCartTotal()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="text-emerald-600 font-semibold">FREE</span>
                </div>
                <div className="flex justify-between text-xl font-bold pt-2 border-t">
                  <span>Total</span>
                  <span className="text-emerald-600">
                    SGD ${getCartTotal()}
                  </span>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="text-xl font-bold mb-2">Contact Information</h3>
              <input
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 outline-none"
                placeholder="Full Name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <input
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 outline-none"
                type="email"
                placeholder="Email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
              <input
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 outline-none"
                placeholder="Phone Number"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />

              <h3 className="text-xl font-bold mt-6 mb-2">Delivery Address</h3>
              <input
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 outline-none"
                placeholder="Street Address"
                required
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 outline-none"
                  placeholder="City"
                  required
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
                <input
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 outline-none"
                  placeholder="Postal Code"
                  required
                  value={formData.postal}
                  onChange={(e) =>
                    setFormData({ ...formData, postal: e.target.value })
                  }
                />
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition mt-4 disabled:opacity-50"
              >
                {isProcessing
                  ? "Processing..."
                  : `Pay SGD $${getCartTotal()} with Stripe`}
              </button>

              <div className="flex items-center justify-center space-x-3 text-sm text-gray-600">
                <Shield className="w-4 h-4" />
                <span>Secure payment via Stripe</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };
  const CartPage = () => {
    if (cart.length === 0) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <ShoppingCart className="w-24 h-24 text-gray-300 mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Your cart is empty
          </h2>
          <p className="text-gray-600 mb-8">
            Add some GlucoNow products to get started!
          </p>
          <button
            onClick={() => setActivePage("shop")}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-3 rounded-full font-bold hover:from-emerald-700 hover:to-teal-700 transition"
          >
            Continue Shopping
          </button>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Shopping Cart</h1>
        <div className="space-y-4 mb-8">
          {cart.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl p-6 shadow-lg flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <img
                  src={IMG.logo}
                  alt="GlucoNow Logo"
                  className="w-12 h-12 rounded-full object-cover shadow-lg"
                />
                <div>
                  <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    GlucoNow
                  </span>
                  <p className="text-xs text-gray-500">
                    Your Daily Glucose Partner
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3 bg-gray-100 rounded-lg p-2">
                  <button
                    onClick={() => updateQuantity(item.id, -1)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold w-8 text-center">
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.id, 1)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-bold text-xl w-24 text-right">
                  SGD ${item.price * item.quantity}
                </p>
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200">
          <div className="space-y-3">
            <div className="flex justify-between text-lg">
              <span>Subtotal</span>
              <span className="font-semibold">SGD ${getCartTotal()}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span>Shipping</span>
              <span className="font-semibold text-emerald-600">FREE</span>
            </div>
            <div className="border-t border-emerald-200 pt-3 flex justify-between text-2xl font-bold">
              <span>Total</span>
              <span className="text-emerald-600">SGD ${getCartTotal()}</span>
            </div>
          </div>
          <button
            onClick={() => setShowCheckout(true)}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition mt-6"
          >
            Proceed to Checkout
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {orderPlaced && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl z-50 animate-pulse-slow">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <p className="font-bold">Order Placed Successfully!</p>
              <p className="text-sm">Thank you for your purchase!</p>
            </div>
          </div>
        </div>
      )}

      {showCheckout && <CheckoutModal />}

      <header className="bg-white/95 backdrop-blur-md sticky top-0 z-40 border-b border-emerald-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img
                src={IMG.logo}
                alt="GlucoNow"
                className="w-12 h-12 rounded-full object-cover shadow"
              />
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  GlucoNow
                </span>
                <p className="text-xs text-gray-500">
                  Your Daily Glucose Partner
                </p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              {[
                { id: "shop", label: "Shop" },
                { id: "story", label: "Our Story" },
                { id: "gallery", label: "Gallery" },
                { id: "contact", label: "Get in Touch" },
                { id: "terms", label: "Terms" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActivePage(item.id)}
                  className={`font-semibold transition ${
                    activePage === item.id
                      ? "text-emerald-600"
                      : "text-gray-600 hover:text-emerald-600"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setActivePage("cart")}
                className="relative flex items-center space-x-2 bg-emerald-600 text-white px-5 py-2.5 rounded-full hover:bg-emerald-700 transition shadow-md"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="font-semibold">Cart ({getCartCount()})</span>
                {getCartCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full font-bold">
                    {getCartCount()}
                  </span>
                )}
              </button>
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2">
              {["shop", "story", "gallery", "contact", "terms"].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    setActivePage(item);
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 rounded-lg capitalize ${
                    activePage === item
                      ? "bg-emerald-50 text-emerald-600"
                      : "text-gray-600"
                  }`}
                >
                  {item === "contact"
                    ? "Get in Touch"
                    : item === "story"
                    ? "Our Story"
                    : item}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {activePage === "cart" && <CartPage />}
      {activePage === "shop" && (
        <div>
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 text-center">
            <p className="text-sm font-semibold px-4">
              🎉 FREE SHIPPING on orders over $50 | HSA-Aligned Quality
            </p>
          </div>

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="relative">
                <div className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg z-10 animate-pulse-slow">
                  BEST SELLER
                </div>
                <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl p-8 shadow-2xl">
                  <div className="aspect-square bg-white rounded-2xl flex items-center justify-center overflow-hidden">
                    <img
                      src={IMG.product}
                      alt="GlucoNow Bottle"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                  {features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="text-center bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition"
                    >
                      <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                        <feature.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="text-xs font-semibold text-gray-900">
                        {feature.title}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                      <p className="text-xs font-semibold">GMP Certified</p>
                    </div>
                    <div className="text-center flex-1 border-x border-gray-200">
                      <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                      <p className="text-xs font-semibold">HSA Aligned</p>
                    </div>
                    <div className="text-center flex-1">
                      <Award className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                      <p className="text-xs font-semibold">SG Trusted</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  <span className="text-gray-600 font-semibold">
                    (500+ positive reviews)
                  </span>
                </div>

                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                  GlucoNow™ Daily Drink
                </h1>
                <p className="text-xl text-emerald-600 font-semibold mb-4">
                  Empowering Singapore's Diabetic Community, One Sip at a Time
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Your daily partner in glucose wellness. A simple yet powerful
                  solution to help regulate blood sugar, naturally and
                  effectively.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  1 bottle contains 500ml.
                </p>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 mb-6 border-2 border-emerald-200">
                  <h3 className="font-bold text-xl mb-4 text-emerald-900 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-2" />
                    Perfect For:
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Type 2 diabetes management",
                      "Prediabetes support",
                      "Daily blood sugar regulation",
                      "Post-meal glucose control",
                      "Natural wellness approach",
                      "Active lifestyle maintenance",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-2xl p-6 mb-6 border-2 border-blue-200">
                  <h3 className="font-bold text-lg mb-3 text-blue-900 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    How to Use:
                  </h3>
                  <p className="text-gray-700 font-medium">
                    Take 50ml after meals, once daily. Shake well, pour, and
                    support your glucose health—hassle-free!
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-bold text-xl mb-4 flex items-center">
                    <ShoppingCart className="w-6 h-6 mr-2 text-emerald-600" />
                    Choose Your Pack:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(packs).map(([key, pack]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedPack(key)}
                        className={`relative p-5 rounded-2xl border-2 transition hover:scale-105 ${
                          selectedPack === key
                            ? "border-emerald-600 bg-emerald-50 shadow-lg"
                            : "border-gray-200 hover:border-emerald-300 bg-white"
                        }`}
                      >
                        {pack.save > 0 && (
                          <div className="absolute -top-3 -right-3 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-md">
                            SAVE {pack.save}%
                          </div>
                        )}
                        <div className="font-bold text-lg text-gray-900">
                          {pack.label}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {pack.qty} bottle{pack.qty > 1 ? "s" : ""}
                        </div>
                        <div className="text-2xl font-bold text-emerald-600 mt-3">
                          SGD ${pack.price}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          ${(pack.price / pack.qty).toFixed(2)} per bottle
                        </div>
                        {selectedPack === key && (
                          <div className="absolute top-2 left-2">
                            <CheckCircle className="w-6 h-6 text-emerald-600 fill-emerald-100" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <button
                    onClick={() => addToCart(selectedPack)}
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-5 rounded-2xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition hover:scale-105 shadow-xl flex items-center justify-center space-x-3"
                  >
                    <ShoppingCart className="w-6 h-6" />
                    <span>Add to Cart - SGD ${packs[selectedPack].price}</span>
                  </button>
                  <button
                    onClick={() => {
                      addToCart(selectedPack);
                      setActivePage("cart");
                    }}
                    className="w-full border-2 border-emerald-600 text-emerald-600 py-5 rounded-2xl font-bold text-lg hover:bg-emerald-50 transition shadow-md"
                  >
                    Buy Now - Express Checkout
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { icon: Shield, text: "Secure Payment" },
                    { icon: Zap, text: "Fast Delivery" },
                    { icon: Heart, text: "Quality Assured" },
                  ].map(({ icon: Icon, text }) => (
                    <div
                      key={text}
                      className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl"
                    >
                      <Icon className="w-6 h-6 text-emerald-600 mb-1" />
                      <span className="text-xs font-semibold text-gray-700">
                        {text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-2xl p-5 text-center shadow-lg">
                  <p className="font-bold text-yellow-900 text-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Limited Stock Available!
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Orders ship within 24 hours
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-16">
              <h2 className="text-3xl font-bold text-center mb-8">
                Real Results from Real People
              </h2>
              <div className="max-w-4xl mx-auto">
                {reviews.map((review, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-3xl p-8 shadow-xl border-2 border-emerald-100"
                  >
                    <div className="flex mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-6 h-6 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                      "{review.text}"
                    </p>
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-lg">
                          {review.name}
                        </p>
                        <p className="text-gray-600">{review.subtitle}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-12 text-center text-white shadow-2xl">
              <h2 className="text-4xl font-bold mb-4">
                Take Control of Your Health Today
              </h2>
              <p className="text-xl mb-8 opacity-95">
                Join thousands managing their glucose levels naturally with
                GlucoNow
              </p>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="bg-white text-emerald-600 px-10 py-5 rounded-full font-bold text-xl hover:bg-gray-100 transition hover:scale-105 shadow-2xl inline-flex items-center"
              >
                Shop Now <ChevronRight className="w-6 h-6 ml-2" />
              </button>
            </div>
          </main>
        </div>
      )}
      {activePage === "story" && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Our Story</h1>
            <p className="text-2xl text-emerald-600 font-semibold">
              Empowering Singapore's Diabetic Community, One Sip at a Time
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              At GlucoNow™, we're more than just a drink—we're your daily
              partner in glucose wellness.
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <Heart className="w-8 h-8 text-emerald-600 mr-3" />
              Our Journey
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              GlucoNow was created with one goal in mind: to make diabetes
              management easier and more sustainable for people in Southeast
              Asia.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              We believed there was a better way. Backed by traditional
              ingredients and modern insights, GlucoNow combines nature and
              science into a delicious daily drink.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you're managing Type 2 diabetes, prediabetes, or simply
              watching your sugar intake, GlucoNow is crafted to support your
              journey—without compromise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border-2 border-emerald-200">
              <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mb-4">
                <Target className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Mission
              </h3>
              <p className="text-gray-700 leading-relaxed">
                To provide a safe, enjoyable, and effective drink that helps
                regulate glucose levels.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 border-2 border-blue-200">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4">
                <Zap className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-700 leading-relaxed">
                To be the leading diabetic-friendly wellness drink brand in
                Asia.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition text-center"
              >
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <Users className="w-8 h-8 text-emerald-600 mr-3" />
              Who We Work With
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              GlucoNow is proudly supported by <strong>JP Recruitment</strong>,
              a Singapore-based medical and health services company.
            </p>
            <ul className="space-y-3 mb-6">
              {[
                "Clinic partnerships",
                "Compliance and HSA alignment",
                "Talent acquisition and training",
                "Healthcare professional outreach",
              ].map((item, idx) => (
                <li key={idx} className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 text-lg">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed">
              Together, we ensure GlucoNow is not just a product—it's part of a
              trusted ecosystem supporting health and lifestyle change.
            </p>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-8 md:p-12 text-white mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Award className="w-8 h-8 mr-3" />
              Manufactured with Care
            </h2>
            <p className="text-lg leading-relaxed">
              Our product is manufactured in Batam, Indonesia by{" "}
              <strong>Rich Baker</strong>, a facility committed to GMP
              standards.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Our Commitment to You
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Shield,
                  title: "Transparency",
                  text: "Clear instructions and honest ingredients",
                },
                {
                  icon: Heart,
                  title: "Sustainability",
                  text: "Ongoing improvement in sourcing and packaging",
                },
                {
                  icon: Users,
                  title: "Community",
                  text: "Knowledge, encouragement, and support",
                },
              ].map(({ icon: Icon, title, text }) => (
                <div key={title}>
                  <Icon className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                  <h3 className="font-bold text-xl mb-2">{title}</h3>
                  <p className="text-gray-600">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Your health journey starts now.
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Take your first step toward better glucose balance with GlucoNow.
            </p>
            <button
              onClick={() => setActivePage("shop")}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:from-emerald-700 hover:to-teal-700 transition hover:scale-105 shadow-xl inline-flex items-center"
            >
              Start Your Journey <ChevronRight className="w-6 h-6 ml-2" />
            </button>
          </div>
        </div>
      )}

      {activePage === "gallery" && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Gallery</h1>
            <p className="text-xl text-gray-600">See GlucoNow in action</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {IMG.gallery.map((src, idx) => (
              <div
                key={src}
                className="bg-white rounded-3xl p-4 shadow hover:shadow-lg transition"
              >
                <img
                  src={src}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-72 object-cover rounded-2xl"
                />
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <button
              onClick={() => setActivePage("shop")}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:from-emerald-700 hover:to-teal-700 transition hover:scale-105 shadow-xl inline-flex items-center"
            >
              Shop GlucoNow <ChevronRight className="w-6 h-6 ml-2" />
            </button>
          </div>
        </div>
      )}
      {activePage === "contact" && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600">We'd love to hear from you</p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
              It all begins with a healthier choice. Your feedback and questions
              inspire us.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200 text-center hover:shadow-lg transition">
                <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-3">Phone</h3>
                <a
                  href="tel:+6590087189"
                  className="text-emerald-600 text-lg font-semibold hover:text-emerald-700"
                >
                  (+65) 9008 7189
                </a>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200 text-center hover:shadow-lg transition">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-3">Email</h3>
                <a
                  href="mailto:Jprecruit.maid@gmail.com"
                  className="text-blue-600 text-lg font-semibold hover:text-blue-700 break-all"
                >
                  Jprecruit.maid@gmail.com
                </a>
              </div>
            </div>

            <div className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Business Hours</h3>
              <p className="text-lg">Mon–Fri: 9:00 AM – 6:00 PM</p>
              <p className="text-lg">Sat: 9:00 AM – 1:00 PM</p>
              <p className="text-lg opacity-75 mt-2">
                We typically respond within 24 hours
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setActivePage("shop")}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:from-emerald-700 hover:to-teal-700 transition hover:scale-105 shadow-xl inline-flex items-center"
            >
              Visit Our Shop <ChevronRight className="w-6 h-6 ml-2" />
            </button>
          </div>
        </div>
      )}

      {activePage === "terms" && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-xl text-gray-600">
              Privacy &amp; Data Protection
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-7 h-7 text-emerald-600 mr-3" />
              Personal Data Protection Act (PDPA)
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              The purpose of the PDPA is to govern the collection, use and
              disclosure of personal data by organizations in a manner that
              recognizes both the right of individuals to protect their personal
              data, and the need of organizations to collect, use or disclose
              personal data for appropriate purposes.
            </p>

            <div className="bg-emerald-50 rounded-2xl p-6 mb-6 border-2 border-emerald-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What is Personal Data?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Data that identifies an individual, whether on its own or
                combined with other information accessible to the organization.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                Examples: unique identifiers (e.g., NRIC), or sets of data such
                as name, age, address, phone, occupation, etc.
              </p>
            </div>

            <div className="bg-blue-50 rounded-2xl p-6 mb-6 border-2 border-blue-200">
              <p className="text-gray-700 leading-relaxed">
                <strong>Important:</strong> PDPA applies to identifiable data.
                It does not apply to anonymized data.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">
              Key PDPA Concepts
            </h3>
            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Consent
                  </h4>
                  <p className="text-gray-700">
                    Collect, use or disclose personal data only with knowledge
                    and consent (with exceptions).
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Purpose
                  </h4>
                  <p className="text-gray-700">
                    Collect, use or disclose data appropriately for the
                    circumstances and inform individuals of the purposes.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Reasonableness
                  </h4>
                  <p className="text-gray-700">
                    Use data only for purposes considered appropriate to a
                    reasonable person in the circumstances.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src={IMG.logo}
                  alt="GlucoNow"
                  className="w-10 h-10 rounded-full"
                />
                <span className="text-xl font-bold">GlucoNow</span>
              </div>
              <p className="text-gray-400 text-sm">
                Your daily partner in glucose wellness
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    onClick={() => setActivePage("shop")}
                    className="hover:text-white transition"
                  >
                    Shop
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActivePage("story")}
                    className="hover:text-white transition"
                  >
                    Our Story
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActivePage("gallery")}
                    className="hover:text-white transition"
                  >
                    Gallery
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    onClick={() => setActivePage("contact")}
                    className="hover:text-white transition"
                  >
                    Contact Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActivePage("terms")}
                    className="hover:text-white transition"
                  >
                    Terms &amp; Privacy
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>(+65) 9008 7189</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span className="break-all">Jprecruit.maid@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 GlucoNow. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Supported by JP Recruitment | Manufactured by Rich Baker, Batam
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
